import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Logo from '@/components/feature/Logo';

function triggerAIAgent() {
  window.open('https://wa.me/201099289280', '_blank');
}

function translateLanguageLabel(lang: string) {
  if (lang.startsWith('ar')) return 'AR';
  return 'EN';
}

function nextLanguage(current: string) {
  return current.startsWith('ar') ? 'en' : 'ar';
}

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 40);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const navLinks = [
    { href: '/#features', label: t('nav.modules') },
    { href: '/#ai-features', label: t('nav.ai_features') },
    { to: '/blog', label: t('nav.blog') },
    { to: '/faq', label: t('nav.faq') },
    { to: '/about', label: t('nav.about') },
    { to: '/contact', label: t('nav.contact') },
  ];

  const isActive = (item: { to?: string; href?: string }) => {
    const path = item.to || item.href || '';
    if (path.startsWith('/#')) return false;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const bgClass = scrolled || !isHome
    ? 'bg-background-50 border-b border-background-200/70'
    : 'bg-transparent';

  const textClass = scrolled || !isHome
    ? 'text-foreground-800'
    : 'text-foreground-950';

  const linkHoverClass = scrolled || !isHome
    ? 'hover:text-primary-600'
    : 'hover:text-primary-600';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${bgClass}`}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-[4.5rem]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <Logo className="w-9 h-9 text-primary-500" />
            <span className={`text-xl md:text-2xl font-bold tracking-tight transition-colors duration-300 ${textClass}`}>
              Texio
            </span>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => {
              const active = isActive(link);
              const baseClass = `px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 whitespace-nowrap ${
                active
                  ? 'text-primary-600 font-semibold'
                  : `${textClass} ${linkHoverClass}`
              }`;

              if (link.href && !link.to) {
                return (
                  <a key={link.href} href={link.href} className={baseClass}>
                    {link.label}
                  </a>
                );
              }

              return (
                <Link key={link.to} to={link.to!} className={baseClass}>
                  {link.label}
                </Link>
              );
            })}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            {/* Language Switcher */}
            <button
              onClick={() => i18n.changeLanguage(nextLanguage(i18n.language))}
              className={`text-xs font-semibold tracking-wider px-2.5 py-1.5 rounded-md border transition-colors duration-200 cursor-pointer whitespace-nowrap ${textClass} ${scrolled || !isHome ? 'border-foreground-200 hover:border-foreground-400' : 'border-foreground-400 hover:border-foreground-600'}`}
              aria-label="Switch language"
            >
              {translateLanguageLabel(i18n.language)}
            </button>

            {/* Book Demo — triggers Readdy Agent */}
            <button
              onClick={triggerAIAgent}
              className="hidden sm:inline-flex items-center gap-1.5 bg-foreground-950 text-background-50 px-4 py-2 rounded-full text-sm font-semibold hover:bg-foreground-800 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              {t('nav.book_demo')}
              <i className="ri-arrow-right-up-line text-sm"></i>
            </button>

            {/* Mobile Hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`md:hidden flex items-center justify-center w-9 h-9 rounded-lg transition-colors duration-200 cursor-pointer ${textClass}`}
              aria-label="Toggle menu"
            >
              <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ${
          mobileOpen ? 'max-h-96 pb-4' : 'max-h-0'
        }`}
      >
        <div className={`px-4 flex flex-col gap-1 border-t ${scrolled || !isHome ? 'border-background-200/70' : 'border-foreground-200/20'}`}>
          {navLinks.map((link) => {
            const active = isActive(link);
            const baseClass = `px-3 py-2.5 text-sm font-medium rounded-lg transition-colors duration-200 ${
              active
                ? 'text-primary-600 font-semibold'
                : `${textClass} ${linkHoverClass}`
            }`;

            if (link.href && !link.to) {
              return (
                <a key={link.href} href={link.href} className={baseClass}>
                  {link.label}
                </a>
              );
            }

            return (
              <Link key={link.to} to={link.to!} className={baseClass}>
                {link.label}
              </Link>
            );
          })}
          <button
            onClick={triggerAIAgent}
            className="mt-2 inline-flex items-center justify-center gap-1.5 bg-foreground-950 text-background-50 px-4 py-2.5 rounded-full text-sm font-semibold hover:bg-foreground-800 transition-colors duration-200 cursor-pointer sm:hidden"
          >
            {t('nav.book_demo')}
            <i className="ri-arrow-right-up-line text-sm"></i>
          </button>
        </div>
      </div>
    </nav>
  );
}